/**
 * Datadog Browser RUM + Logs for the web apps (landing page, sleep diary, health carer).
 *
 * One shared initializer so the three apps use the same conventions:
 *  - `GATSBY_DATADOG_*` env vars (the `GATSBY_` prefix is what Gatsby exposes to the browser; the
 *    webpack apps read the same names through dotenv-webpack so one Netlify env block fits all);
 *  - same client token for RUM and Logs, so logs are attached to the RUM session;
 *  - errors reported through `trackError` land in RUM Error Tracking (the Sentry "issue" of old).
 *
 * The SDK also registers `window.DD_RUM` / `window.DD_LOGS` — that is how the mobile app's
 * WebView (`@datadog/mobile-react-native-webview`) stitches a web page into the native session.
 *
 * https://docs.datadoghq.com/real_user_monitoring/browser/setup/client/
 * https://docs.datadoghq.com/logs/log_collection/javascript/
 */
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';
import { hasWindow } from '@web/utils/platform';

export interface DatadogWebOptions {
  /** Unified service tag, e.g. `vigilantes-do-sono-landing-page`. */
  service: string;
  /** Build version (package.json version). Required for Error Tracking to group by release. */
  version?: string;
  /** Overrides GATSBY_DATADOG_ENV. */
  env?: string;
  /**
   * Hosts (or URL prefixes) that should receive `x-datadog-*` / `traceparent` headers so browser
   * requests connect to backend traces. Every host listed MUST answer CORS preflights allowing
   * those headers, otherwise the request itself breaks — which is why this is opt-in and empty by
   * default. Falsy entries are ignored.
   */
  allowedTracingUrls?: Array<string | undefined>;
  /** Scrub PII from events before they leave the browser (return `false` to drop the event). */
  beforeSend?: RumInitConfiguration['beforeSend'];
  /** Defaults to `NODE_ENV !== 'development'` unless GATSBY_DATADOG_FORCE_ENABLE=true. */
  enabled?: boolean;
}

const readNumber = (raw: string | undefined, fallback: number): number => {
  if (raw === undefined || raw === '') return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const splitList = (raw: string | undefined): string[] =>
  (raw ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

let initialized = false;

export const isDatadogWebInitialized = (): boolean => initialized;

/**
 * Initializes RUM + Logs. Safe to call more than once and on the server (no-op there).
 * Returns whether the SDK was actually started.
 */
export const initDatadogWeb = (options: DatadogWebOptions): boolean => {
  if (!hasWindow() || initialized) return initialized;

  const clientToken = process.env.GATSBY_DATADOG_CLIENT_TOKEN;
  const applicationId = process.env.GATSBY_DATADOG_APPLICATION_ID;
  const forceEnable = process.env.GATSBY_DATADOG_FORCE_ENABLE === 'true';
  const enabled = options.enabled ?? (forceEnable || process.env.NODE_ENV !== 'development');

  if (!enabled) return false;
  if (!clientToken || !applicationId) {
    // eslint-disable-next-line no-console
    console.info('[datadog] RUM disabled: GATSBY_DATADOG_CLIENT_TOKEN / GATSBY_DATADOG_APPLICATION_ID not set');
    return false;
  }

  const site = process.env.GATSBY_DATADOG_SITE || 'datadoghq.com';
  const env = options.env ?? process.env.GATSBY_DATADOG_ENV ?? process.env.GATSBY_ENV ?? process.env.NODE_ENV ?? 'production';
  const sessionSampleRate = readNumber(process.env.GATSBY_DATADOG_SESSION_SAMPLE_RATE, 100);
  const sessionReplaySampleRate = readNumber(process.env.GATSBY_DATADOG_REPLAY_SAMPLE_RATE, 20);
  const tracingUrls = [...splitList(process.env.GATSBY_DATADOG_TRACING_URLS), ...(options.allowedTracingUrls ?? [])].filter(
    (url): url is string => !!url
  );

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service: options.service,
    env,
    version: options.version,
    sessionSampleRate,
    sessionReplaySampleRate,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackViewsManually: false,
    // Health data on screen: replay masks every input; action names never include typed text.
    defaultPrivacyLevel: 'mask-user-input',
    enablePrivacyForActionName: true,
    allowedTracingUrls: tracingUrls.map((match) => ({ match, propagatorTypes: ['datadog', 'tracecontext'] })),
    traceSampleRate: 100,
    telemetrySampleRate: 20,
    beforeSend: options.beforeSend,
  });

  datadogLogs.init({
    clientToken,
    site,
    service: options.service,
    env,
    version: options.version,
    sessionSampleRate,
    // Uncaught exceptions, failed network requests and console.error/warn become log events.
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error', 'warn'],
    telemetrySampleRate: 20,
  });

  initialized = true;
  return true;
};

// type alias (not interface): the SDK's User type has an index signature, which interfaces don't satisfy
export type DatadogWebUser = {
  id: string;
  name?: string;
  email?: string;
};

/** Identifies the current user on RUM sessions and logs; `null` clears it (logout). */
export const setDatadogUser = (user: DatadogWebUser | null): void => {
  if (!initialized) return;
  if (user) {
    datadogRum.setUser(user);
    datadogLogs.setUser(user);
  } else {
    datadogRum.clearUser();
    datadogLogs.clearUser();
  }
};

/** Reports a handled error to RUM Error Tracking (plus a correlated log line). */
export const trackError = (error: unknown, context?: Record<string, unknown>): void => {
  if (!initialized) return;
  datadogRum.addError(error, context);
};

/** Reports a message that is not an exception (the old `Sentry.captureMessage`). */
export const trackMessage = (message: string, level: 'info' | 'warn' | 'error' = 'warn', context?: Record<string, unknown>): void => {
  if (!initialized) return;
  datadogLogs.logger[level](message, context);
};
